// @flow

import { ThemeProvider } from '@emotion/react'
import styled from '@emotion/styled'
import { Link, graphql, withPrefix } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'
import { Parallax } from 'react-scroll-parallax'

import { colors, fonts, media, themes } from '@toggl/style'
import {
  Button,
  CallToAction,
  Card,
  CarouselDotMobile,
  Hero,
  Hire,
  Plan,
  Responsive,
  SectionBlock,
  SectionDiagonal,
  Track,
  useAutoPlayRef,
} from '@toggl/ui'
import HeaderAnnouncement from 'components/HeaderAnnouncement'
import { Gif } from 'components/image'
import { LayoutGlobal } from 'components/layouts'
import { Video } from 'components/video'

import typeof { HeaderAnnouncementYaml } from '../../components/HeaderAnnouncement/data.yml'

import typeof { ShoutOuts } from './data.yml'

declare var globalThis: any

const IndexPage = ({
  data,
}: {
  data: {
    headerAnnouncementYaml: HeaderAnnouncementYaml,
    indexYaml: { shoutOuts: ShoutOuts },
  },
}) => {
  const announcement = data.headerAnnouncementYaml?.announcements?.slice().pop()

  return (
    <LayoutGlobal
      announcement={
        announcement && <HeaderAnnouncement data={announcement.text} />
      }
      title={
        'Toggl: Time Tracking, Project Planning and Hiring Tools to Help Teams Work Better'
      }
      description={
        'We take the stress out of time tracking, project-planning, and hiring. Designed by and for teams that work from anywhere.'
      }
      ogImage={require('assets/toggl-global-main.jpg')}
      favicon={require('assets/favicon-global.png')}
    >
      <Hero.Wrapper
        bgImage={
          <StaticImage
            src="../../assets/homepage-hero-bg.jpg"
            loading="eager"
            alt="Hero background image"
            layout="fullWidth"
            placeholder="none"
            objectFit="cover"
            backgroundColor="black"
          />
        }
        bgVideo={require('assets/bg-video.mp4')}
      >
        <ThemeProvider theme={themes.track}>
          <Hero.ContentWrapper>
            <Hero.Title>
              Time tracking for
              <br />
              <Rotalic>better</Rotalic> work,
              <br />
              not overwork
            </Hero.Title>

            <LogoWrapper>
              <Track />
            </LogoWrapper>

            <Hero.Text>
              Reliable time tracking software loved by 5M+ users
            </Hero.Text>

            <Hero.CtaSection>
              <Link to={'/track/signup/'}>
                <Button.Primary>Sign up for free</Button.Primary>
              </Link>
              <Link to={'/track/'}>
                <Button.Secondary
                  caretDirection="right"
                  color={colors.almostWhite}
                >
                  Or discover more
                </Button.Secondary>
              </Link>
            </Hero.CtaSection>
          </Hero.ContentWrapper>
        </ThemeProvider>
      </Hero.Wrapper>
      <SectionBlock background={colors.fadedPurple} flex>
        <CardWrapper>
          <ThemeProvider theme={themes.plan}>
            <Card.Product
              logo={<Plan />}
              title={'Beautifully simple project planning'}
              video={
                <Video
                  width={160}
                  height={160}
                  src={require('assets/toggl-plan.mp4')}
                />
              }
              Cta={() => (
                <a href={withPrefix('/plan/')}>
                  <Responsive.DesktopOnly>
                    <Button.Primary>Start planning for free</Button.Primary>
                  </Responsive.DesktopOnly>
                  <Responsive.MobileOnly>
                    <Button.Primary>Start planning</Button.Primary>
                  </Responsive.MobileOnly>
                </a>
              )}
              discoverMore={
                <a href={withPrefix('/plan/product')}>or discover features</a>
              }
              shoutOut={data.indexYaml.shoutOuts.plan}
            />
          </ThemeProvider>
          <ThemeProvider theme={themes.track}>
            <Card.Product
              logo={<Track />}
              title={'Effortless time tracking and reporting'}
              video={
                <Video
                  width={160}
                  height={160}
                  src={require('assets/toggl-track.mp4')}
                />
              }
              Cta={() => (
                <Link to="/track/">
                  <Responsive.DesktopOnly>
                    <Button.Primary>Start tracking for free</Button.Primary>
                  </Responsive.DesktopOnly>
                  <Responsive.MobileOnly>
                    <Button.Primary>Start tracking</Button.Primary>
                  </Responsive.MobileOnly>
                </Link>
              )}
              discoverMore={
                <Link to="/track/features/">or discover features</Link>
              }
              shoutOut={data.indexYaml.shoutOuts.track}
            />
          </ThemeProvider>
          <ThemeProvider theme={themes.hire}>
            <Card.Product
              logo={<Hire />}
              title={'Smarter hiring with skills tests'}
              video={
                <Video
                  width={160}
                  height={160}
                  src={require('assets/toggl-hire.mp4')}
                />
              }
              Cta={() => (
                <a href={withPrefix('/hire/')}>
                  <Responsive.DesktopOnly>
                    <Button.Primary>Start hiring for free</Button.Primary>
                  </Responsive.DesktopOnly>
                  <Responsive.MobileOnly>
                    <Button.Primary>Start hiring</Button.Primary>
                  </Responsive.MobileOnly>
                </a>
              )}
              discoverMore={
                <a href={withPrefix('/hire/product')}>or discover features</a>
              }
              shoutOut={data.indexYaml.shoutOuts.hire}
            />
          </ThemeProvider>
        </CardWrapper>
      </SectionBlock>
      <SectionBlock center background={colors.fadedPurple}>
        <CtaOneWrapper>
          <CallToAction
            size={1}
            title={
              <span>
                Work <Rotalic>Smarter</Rotalic>,
                <br />
                Live Happier
              </span>
            }
            subTitle={
              <>
                <span>
                  Our global team comes together every day to move our mission
                  forward - making simple, elegant tools to help you be
                  productive without the stress
                </span>
                <MissionLinkWrapper>
                  <Link to="/mission/">
                    <Button.Secondary
                      caretDirection="right"
                      color={colors.beige}
                    >
                      See what drives us
                    </Button.Secondary>
                  </Link>
                </MissionLinkWrapper>
              </>
            }
          />

          <Responsive.DesktopOnly>
            <VideoStyled
              loop
              muted
              poster={require('assets/killstress-placeholder.png')}
              preload="none"
              ref={useAutoPlayRef()}
              width="700"
              height="650"
            >
              <source src={require('assets/killstress.mp4')} />
            </VideoStyled>
          </Responsive.DesktopOnly>
          <Responsive.MobileOnly>
            <StyledImage
              src={require('assets/killstress-placeholder.png')}
              alt={'Work smarter, live happier'}
              loading="lazy"
              width="650"
              height="366"
            />
          </Responsive.MobileOnly>
        </CtaOneWrapper>
      </SectionBlock>

      <ArticleSection
        topBg={colors.fadedPurple}
        bottomBg={colors.fadedPurpleAlt}
      >
        <SectionDiagonal.Wrapper background={colors.fadedPurpleAlt}>
          <SectionDiagonal.Content>
            <Wrapper>
              <CtaParagraphSectionSecond>
                <Responsive.DesktopOnly>
                  <Parallax y={[-30, 30]}>
                    <CallToAction
                      size={2}
                      title={<span>Knowledge is Power</span>}
                      subTitle={
                        <span>
                          Get your remote-working, time tracking,
                          project-planning, and hiring best practices here!
                          Straight from the experts at Toggl.
                        </span>
                      }
                      Cta={() => (
                        <Link href="https://toggl.com/blog/">
                          <Button.Primary>See More on Our Blog</Button.Primary>
                        </Link>
                      )}
                      image={
                        <Gif
                          src={require('assets/megaphone-dark.gif')}
                          loading="lazy"
                          alt="Megaphone jumping and emitting sound"
                          placeholder={
                            <StaticImage
                              src="../../assets/megaphone-dark-still.png"
                              layout="fixed"
                              width={318}
                              height={220}
                              placeholder="none"
                              alt="Megaphone emitting sound"
                            />
                          }
                        />
                      }
                    />
                  </Parallax>
                </Responsive.DesktopOnly>
                <Responsive.MobileOnly>
                  <Parallax y={[-20, 15]}>
                    <CallToAction
                      size={2}
                      title={<span>Knowledge is Power</span>}
                      subTitle={
                        <span>
                          Get your remote-working, time tracking,
                          project-planning, and hiring best practices here!
                          Straight from the experts at Toggl.
                        </span>
                      }
                      Cta={() => (
                        <Link href="https://toggl.com/blog/">
                          <Button.Primary>See More on Our Blog</Button.Primary>
                        </Link>
                      )}
                      image={
                        <StaticImage
                          src="../../assets/megaphone.png"
                          layout="fixed"
                          width={160}
                          height={110}
                          alt="Megaphone emitting sound"
                        />
                      }
                    />
                  </Parallax>
                </Responsive.MobileOnly>
              </CtaParagraphSectionSecond>
            </Wrapper>

            <CarouselDotMobile>
              <Card.ReadMore
                title={'How To Work From Home'}
                subtitle={
                  'Everything we’ve learned from the last seven years of remote work'
                }
                link={
                  <a
                    href="https://toggl.com/work-from-home/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button.Secondary caretDirection="right">
                      Read more
                    </Button.Secondary>
                  </a>
                }
                image={
                  <StaticImage
                    src={'../../assets/resource-work-from-home.png'}
                    alt={'Toggl Work From Home Hub'}
                    width={669}
                    sizes="(max-width: 490px) 360px, (min-width: 800px) 360px, 669px"
                    placeholder="none"
                  />
                }
              />
              <Card.ReadMore
                title={'The Expectations Game'}
                subtitle={
                  'The secret behind any happy collaboration usually lies in the skill of managing expectations'
                }
                link={
                  <a
                    href="https://toggl.com/plan/blueprint-the-expectations-game/index"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button.Secondary caretDirection="right">
                      Read more
                    </Button.Secondary>
                  </a>
                }
                image={
                  <StaticImage
                    src="../../assets/resource-toggl-plan-blueprint.png"
                    alt={'The Expectations Game Blueprint'}
                    width={669}
                    sizes="(max-width: 490px) 360px, (min-width: 800px) 360px, 669px"
                    placeholder="none"
                  />
                }
              />
              <Card.ReadMore
                title={'Hiring Remotely'}
                subtitle={'Hiring Remotely: A Practical Guide'}
                link={
                  <a
                    href="https://ebook.hire.toggl.com/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button.Secondary caretDirection="right">
                      Read more
                    </Button.Secondary>
                  </a>
                }
                image={
                  <StaticImage
                    src="../../assets/resource-hiring-remotely.png"
                    alt={'Hiring Remotely Online Guide'}
                    width={669}
                    sizes="(max-width: 490px) 360px, (min-width: 800px) 360px, 669px"
                    placeholder="none"
                  />
                }
              />
            </CarouselDotMobile>
          </SectionDiagonal.Content>
        </SectionDiagonal.Wrapper>
      </ArticleSection>
    </LayoutGlobal>
  )
}

export const query = graphql`
  {
    headerAnnouncementYaml(
      announcements: { elemMatch: { active: { eq: true } } }
    ) {
      announcements {
        text {
          body
        }
      }
    }
    indexYaml {
      shoutOuts {
        track {
          text
          url
          active
        }
        plan {
          text
          url
          active
        }
        hire {
          text
          url
          active
        }
      }
    }
  }
`

const CtaOneWrapper = styled.div`
  padding-bottom: 50px;
`
const StyledImage = styled.img`
  width: 90%;
  margin: -70px auto 30px;
  object-fit: contain;
`

const VideoStyled = styled.video`
  position: relative;
  width: 100%;
  min-height: 320px;
  max-width: 450px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center center;
  margin: -60px auto 60px;
  z-index: 0;
  ${media.mq[1]} {
    min-height: 650px;
    max-width: 700px;
    margin: -120px auto 120px;
  }
`

const CardWrapper = styled.div`
  ${media.mq[1]} {
    display: flex;
    flex-direction: column;
    margin: auto;
  }
  ${media.mq[2]} {
    flex-direction: row;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: auto;
`

const MissionLinkWrapper = styled.span`
  margin-top: 20px;
  display: block;
`

const CtaParagraphSection = styled.div`
  padding: 0 30px 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    ${media.mq[1]} {
      max-width: 840px;
    }
  }

  ${media.mq[1]} {
    padding: 0;
  }
`

const CtaParagraphSectionSecond = styled(CtaParagraphSection)`
  margin-top: -100px;
  padding-bottom: 70px;
  ${media.mq[1]} {
    margin-top: -250px;
    padding-bottom: 150px;
  }
`

const ArticleSection = styled(SectionDiagonal.Background)`
  > div {
    overflow: visible;
    > div {
      padding: 50px 0 0;
    }
    ${media.mq[1]} {
      padding: 50px 30px 25px;
    }
  }
`

const LogoWrapper = styled.div`
  svg {
    width: 120px;
  }
  ${media.mq[1]} {
    svg {
      width: 167px;
    }
  }
`

const Rotalic = styled.span`
  font-family: ${fonts.gtHaptikMediumRotalic};
  color: ${(p) => p.theme.primary};
`

export default IndexPage
